import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Calendar, 
  Clock, 
  User, 
  Building2, 
  CreditCard,
  CheckCircle2,
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OnboardingProject, OnboardingStatus } from '@/types/onboarding';
import { cn } from '@/lib/utils';
import ProgressDonut from './ProgressDonut';

interface OnboardingProjectCardProps {
  project: OnboardingProject;
  onView: (id: string) => void;
  onArchive?: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onDelete?: (id: string) => void;
  formatStatus: (status: OnboardingStatus) => { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' };
  formatStage: (stage: string) => string;
  importedCounts?: { properties: number; units: number; accounts: number };
  isArchived?: boolean;
}

export default function OnboardingProjectCard({
  project,
  onView,
  onArchive,
  onUnarchive,
  onDelete,
  formatStatus,
  formatStage,
  importedCounts = { properties: 0, units: 0, accounts: 0 },
  isArchived = false,
}: OnboardingProjectCardProps) {
  const statusInfo = formatStatus(project.overall_status);
  const isCompleted = project.overall_status === 'live' || (project.overall_status as string) === 'completed';
  const progress = project.completion_percentage || 0;
  
  const getDaysUntilGoLive = () => {
    if (!project.target_go_live_date) return null;
    const today = new Date();
    const goLive = new Date(project.target_go_live_date);
    const diffTime = goLive.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const daysLeft = getDaysUntilGoLive();
  const isOverdue = daysLeft !== null && daysLeft < 0;
  const isDueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

  // Get logo or generate initials avatar
  const getLogoUrl = () => {
    if (project.logo_url) return project.logo_url;
    const initials = project.customer_name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
    const colors = ['3b82f6', '10b981', 'f59e0b', 'ef4444', '8b5cf6', '06b6d4', '84cc16', 'f97316'];
    const colorIndex = project.customer_name.length % colors.length;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${colors[colorIndex]}&color=fff&size=128&bold=true`;
  };

  const getStageIcon = () => {
    if (isCompleted) return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    return <Clock className="h-4 w-4 text-blue-600" />;
  };

  const formatNumber = (num: number | null | undefined) => (num ?? 0).toLocaleString('en-US');

  const getProgressColor = () => {
    if (progress >= 80) return 'hsl(142, 76%, 36%)';
    if (progress >= 50) return 'hsl(45, 93%, 47%)';
    return 'hsl(221, 83%, 53%)';
  };

  return (
    <Card className={cn(
      "group hover:shadow-lg transition-all duration-200 border-l-4",
      isArchived ? "border-l-muted-foreground opacity-75" : "border-l-primary"
    )}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Customer Logo */}
          <div className="flex-shrink-0">
            <div className="relative">
              <img
                src={getLogoUrl()}
                alt={project.customer_name}
                className="w-14 h-14 rounded-lg object-cover border-2 border-border shadow-sm"
                onError={(e) => {
                  const initials = project.customer_name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=6366f1&color=fff&size=128&bold=true`;
                }}
              />
              {isCompleted && !isArchived && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-background flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="h-3 w-3 text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-base leading-tight truncate text-foreground">
                    {project.customer_name}
                  </h3>
                  <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0.5 h-5 flex-shrink-0">
                    {statusInfo.label}
                  </Badge>
                </div>
              </div>

              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onView(project.id)}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {isArchived ? (
                    <>
                      {onUnarchive && (
                        <DropdownMenuItem onClick={() => onUnarchive(project.id)}>
                          <ArchiveRestore className="h-4 w-4 mr-2" />
                          Restore
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem 
                          onClick={() => onDelete(project.id)} 
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Permanently
                        </DropdownMenuItem>
                      )}
                    </>
                  ) : (
                    onArchive && (
                      <DropdownMenuItem onClick={() => onArchive(project.id)}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    )
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Stats Grid */}
            <div className="flex items-center gap-4 mb-3.5">
              <div className="flex-shrink-0">
                <ProgressDonut
                  value={progress}
                  size={56}
                  strokeWidth={6}
                  progressColor={getProgressColor()}
                  showLabel={true}
                  labelClassName="text-[10px]"
                />
              </div>

              <div className="flex-1 grid grid-cols-2 gap-2">
                <div className="flex flex-col justify-between p-2 bg-muted/30 rounded-md border border-border/50">
                  <div className="flex items-center gap-1 mb-1">
                    {getStageIcon()}
                    <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Stage</p>
                  </div>
                  <p className="text-xs font-bold text-foreground leading-tight line-clamp-1">
                    {formatStage(project.current_stage)}
                  </p>
                </div>

                <div className={cn(
                  "flex flex-col justify-between p-2 rounded-md border",
                  isOverdue ? "bg-red-50/80 border-red-200" :
                  isDueSoon ? "bg-amber-50/80 border-amber-200" :
                  "bg-muted/30 border-border/50"
                )}>
                  <div className="flex items-center gap-1 mb-1">
                    <Calendar className={cn(
                      "h-3.5 w-3.5 flex-shrink-0",
                      isOverdue ? "text-red-600" : isDueSoon ? "text-amber-600" : "text-blue-600"
                    )} />
                    <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Days Left</p>
                  </div>
                  <p className={cn(
                    "text-xs font-bold leading-tight",
                    isOverdue && "text-red-700",
                    isDueSoon && "text-amber-700",
                    !isOverdue && !isDueSoon && "text-foreground"
                  )}>
                    {daysLeft !== null ? (isOverdue ? `${Math.abs(daysLeft)} overdue` : `${daysLeft}`) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2.5 border-t border-border/50">
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-1 min-w-0">
                {project.customer_contact_name && (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate max-w-[80px]">{project.customer_contact_name}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <CreditCard className="h-3.5 w-3.5 text-purple-600" />
                  <span>{formatNumber(project.estimated_accounts || 0)}</span>
                </div>
                {importedCounts.properties > 0 && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Building2 className="h-3.5 w-3.5" />
                    <span>{formatNumber(importedCounts.properties)}</span>
                  </div>
                )}
              </div>

              <Button
                onClick={() => onView(project.id)}
                size="sm"
                variant={isArchived ? 'outline' : 'default'}
                className="gap-1.5 h-8 px-3 text-xs flex-shrink-0"
              >
                {isArchived ? 'View' : isCompleted ? 'Manage' : 'Continue'}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
